import React from 'react'
import { useTranslation } from 'react-i18next'
import { AppLayout } from '../../layouts'
import { Link } from 'react-router-dom'
import { Routes } from '../../routes'
import { tenantBranding } from '../../services'
import { useTheme } from '../../contexts/ThemeContext'
import { getTenantLogo } from '../../constants/Images'
import { getTenantName } from '../../utils/brandingStyles'

const PrivacyPolicy = () => {
    const { t } = useTranslation()
    const { theme } = useTheme()

    return (
        <AppLayout>
            {/* Header with Logo */}
            <div className="container-fluid fixed-top mb-3 bg-black d-flex w-100">
                <div className="text-white fs-5 mb-0 pt-2" style={{ height: '50px' }}>
                    <span>
                        <img className="img-fluid" src={getTenantLogo(theme)} alt={getTenantName()} style={{ height: '30px' }} />
                    </span>
                </div>
            </div>
            <span className="mb-5 text-black">.</span>

            <div className="min-vh-100 w-100" style={{ overflowX: 'hidden' }}>
                {/* Back Navigation */}
                <div className="container my-4">
                    <Link to={Routes.HOMEVIEW} className="text-light fw-bold fs-4 text-decoration-none text-uppercase">
                        <div className="d-flex align-items-center">
                            <span><i className="fa-solid fa-arrow-left"></i></span>
                            <span className="ms-2">{t('navigation.backHome')}</span>
                        </div>
                    </Link>
                </div>

                <main className="w-100" style={{ overflow: 'hidden' }}>
                    {/* Hero Section */}
                    <section className="position-relative py-5" style={{ 
                        paddingTop: '2rem', 
                        paddingBottom: '5rem',
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.9) 50%, rgba(0,0,0,1) 100%)'
                    }}>
                        <div className="container" style={{ maxWidth: '56rem' }}>
                            <div className="text-center">
                                <h1 className="display-3 fw-semibold text-white lh-base mb-4" style={{ 
                                    fontSize: 'clamp(2.5rem, 5vw, 3.5rem)' 
                                }}>
                                    {t('views.legalDocs.privacyPolicy.title')}
                                </h1>
                                <p className="fs-5 text-white-50 lh-base mx-auto" style={{ maxWidth: '42rem' }}>
                                    {t('views.legalDocs.privacyPolicy.subtitle')}
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Content Section */}
                    <section className="py-5 px-3 w-100" style={{ paddingTop: '4rem' }}>
                        <div className="container mx-auto w-100" style={{ maxWidth: '56rem' }}>
                            <div style={{ gap: '3rem' }} className="d-flex flex-column">
                                {/* Introduction */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('views.legalDocs.privacyPolicy.introduction.title')}
                                    </h2>
                                    <p className="text-white-50 lh-base mb-3">
                                        {t('views.legalDocs.privacyPolicy.introduction.p1')}
                                    </p>
                                    <p className="text-white-50 lh-base mb-3">
                                        {t('views.legalDocs.privacyPolicy.introduction.p2')}
                                    </p>
                                    <p className="text-white-50 lh-base">
                                        {t('views.legalDocs.privacyPolicy.introduction.p3')}
                                    </p>
                                </div>

                                {/* Information We Collect */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('views.legalDocs.privacyPolicy.informationWeCollect.title')}
                                    </h2>
                                    <div className="d-flex flex-column" style={{ gap: '1.5rem' }}>
                                        <div>
                                            <h3 className="h5 fw-semibold text-white mb-2">
                                                {t('views.legalDocs.privacyPolicy.informationWeCollect.personalInfo.title')}
                                            </h3>
                                            <p className="text-white-50 mb-3">
                                                {t('views.legalDocs.privacyPolicy.informationWeCollect.personalInfo.description')}
                                            </p>
                                            <ul className="text-white-50" style={{ 
                                                listStyleType: 'disc', 
                                                paddingLeft: '1.5rem',
                                                lineHeight: '1.6'
                                            }}>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.personalInfo.item1')}</li>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.personalInfo.item2')}</li>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.personalInfo.item3')}</li>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.personalInfo.item4')}</li>
                                            </ul>
                                        </div>

                                        <div>
                                            <h3 className="h5 fw-semibold text-white mb-2">
                                                {t('views.legalDocs.privacyPolicy.informationWeCollect.automaticInfo.title')}
                                            </h3>
                                            <p className="text-white-50 mb-3">
                                                {t('views.legalDocs.privacyPolicy.informationWeCollect.automaticInfo.description')}
                                            </p>
                                            <ul className="text-white-50" style={{ 
                                                listStyleType: 'disc', 
                                                paddingLeft: '1.5rem',
                                                lineHeight: '1.6'
                                            }}>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.automaticInfo.item1')}</li>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.automaticInfo.item2')}</li>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.automaticInfo.item3')}</li>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.automaticInfo.item4')}</li>
                                            </ul>
                                        </div>

                                        <div>
                                            <h3 className="h5 fw-semibold text-white mb-2">
                                                {t('views.legalDocs.privacyPolicy.informationWeCollect.thirdPartyInfo.title')}
                                            </h3>
                                            <p className="text-white-50">
                                                {t('views.legalDocs.privacyPolicy.informationWeCollect.thirdPartyInfo.description')}
                                            </p>
                                            <ul className="text-white-50" style={{ 
                                                listStyleType: 'disc', 
                                                paddingLeft: '1.5rem',
                                                lineHeight: '1.6'
                                            }}>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.thirdPartyInfo.item1')}</li>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.thirdPartyInfo.item2')}</li>
                                                <li className="mb-2">{t('views.legalDocs.privacyPolicy.informationWeCollect.thirdPartyInfo.item3')}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* How We Use Your Information */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('views.legalDocs.privacyPolicy.howWeUseYourInfo.title')}
                                    </h2>
                                    <p className="text-white-50 mb-3">
                                        {t('views.legalDocs.privacyPolicy.howWeUseYourInfo.description')}
                                    </p>
                                    <ul className="text-white-50" style={{ 
                                        listStyleType: 'disc', 
                                        paddingLeft: '1.5rem',
                                        lineHeight: '1.6'
                                    }}>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.howWeUseYourInfo.item1')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.howWeUseYourInfo.item2')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.howWeUseYourInfo.item3')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.howWeUseYourInfo.item4')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.howWeUseYourInfo.item5')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.howWeUseYourInfo.item6')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.howWeUseYourInfo.item7')}</li>
                                    </ul>
                                </div>

                                {/* Data Security */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('views.legalDocs.privacyPolicy.dataSecurity.title')}
                                    </h2>
                                    <p className="text-white-50 mb-3">
                                        {t('views.legalDocs.privacyPolicy.dataSecurity.description')}
                                    </p>
                                    <ul className="text-white-50" style={{ 
                                        listStyleType: 'disc', 
                                        paddingLeft: '1.5rem',
                                        lineHeight: '1.6'
                                    }}>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.dataSecurity.item1')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.dataSecurity.item2')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.dataSecurity.item3')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.dataSecurity.item4')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.dataSecurity.item5')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.dataSecurity.item6')}</li>
                                    </ul>
                                </div>

                                {/* Your Rights */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('views.legalDocs.privacyPolicy.yourRights.title')}
                                    </h2>
                                    <p className="text-white-50 mb-3">
                                        {t('views.legalDocs.privacyPolicy.yourRights.description')}
                                    </p>
                                    <ul className="text-white-50" style={{ 
                                        listStyleType: 'disc', 
                                        paddingLeft: '1.5rem',
                                        lineHeight: '1.6'
                                    }}>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.yourRights.item1')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.yourRights.item2')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.yourRights.item3')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.yourRights.item4')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.yourRights.item5')}</li>
                                        <li className="mb-2">{t('views.legalDocs.privacyPolicy.yourRights.item6')}</li>
                                    </ul>
                                </div>

                                {/* Data Retention */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('views.legalDocs.privacyPolicy.dataRetention.title')}
                                    </h2>
                                    <p className="text-white-50 lh-base">
                                        {t('views.legalDocs.privacyPolicy.dataRetention.description')}
                                    </p>
                                </div>

                                {/* Intellectual Property */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('views.legalDocs.privacyPolicy.intellectualProperty.title')}
                                    </h2>
                                    <p className="text-white-50 lh-base">
                                        {t('views.legalDocs.privacyPolicy.intellectualProperty.description')}
                                    </p>
                                </div>

                                {/* Third-Party Services */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('views.legalDocs.privacyPolicy.thirdPartyServices.title')}
                                    </h2>
                                    <p className="text-white-50 lh-base mb-3">
                                        {t('views.legalDocs.privacyPolicy.thirdPartyServices.p1')}
                                    </p>
                                    <p className="text-white-50 lh-base mb-3">
                                        {t('views.legalDocs.privacyPolicy.thirdPartyServices.p2')}
                                    </p>
                                </div>

                                {/* Cookies and Tracking */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('views.legalDocs.privacyPolicy.cookiesAndTracking.title')}
                                    </h2>
                                    <p className="text-white-50 lh-base mb-3">
                                        {t('views.legalDocs.privacyPolicy.cookiesAndTracking.p1')}
                                    </p>
                                    <p className="text-white-50 lh-base mb-3">
                                        {t('views.legalDocs.privacyPolicy.cookiesAndTracking.p2')}
                                    </p>
                                </div>

                                {/* Contact Us */}
                                <div>
                                    <h2 className="h3 fw-bold text-white mb-3">
                                        {t('navigation.contactUs')}
                                    </h2>
                                    <p className="text-white-50 mb-3">
                                        {t('views.legalDocs.privacyPolicy.contact.description')}
                                    </p>
                                    <div className="border rounded p-4" style={{ 
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        borderColor: 'rgba(255,255,255,0.1)'
                                    }}>
                                        <p className="text-white fw-medium mb-1">
                                            {t('views.legalDocs.privacyPolicy.contact.teamName')}
                                        </p>
                                        <p className="text-white-50 mb-1">
                                            {t('views.legalDocs.privacyPolicy.contact.email', { supportEmail: tenantBranding.supportEmail })}
                                        </p>
                                        <p className="text-white-50 mb-0">
                                            {t('views.legalDocs.privacyPolicy.contact.address')}
                                        </p>
                                    </div>
                                </div>

                                {/* Last Updated */}
                                <div className="border-top pt-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                                    <p className="small text-white-50">
                                        {t('views.legalDocs.privacyPolicy.lastUpdated')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </AppLayout>
    )
}

export default PrivacyPolicy